/**
 * Custom hook for interacting with localStorage
 */
import { useState, useEffect, useCallback } from 'react';
import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';

/**
 * Hook for persistent storage with localStorage
 * @param {string} key - The localStorage key
 * @param {any} initialValue - The initial value if key doesn't exist
 * @returns {Array} - [storedValue, setValue, removeValue]
 */
export function useLocalStorage(key, initialValue) {
  // Get initial value from localStorage or use provided initialValue
  const readValue = useCallback(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      if (item === null) return initialValue;
      
      // Essayer d'abord de parser comme JSON, puis utiliser directement la valeur si échec
      try {
        return JSON.parse(item);
      } catch (e) {
        // Si le parsing échoue, c'est probablement une chaîne simple, on la retourne telle quelle
        return item;
      }
    } catch (error) {
      debug.warn(config, `Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  }, [initialValue, key]);

  // State to store the value
  const [storedValue, setStoredValue] = useState(readValue);

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue = useCallback(
    (value) => {
      if (typeof window === 'undefined') {
        debug.warn(config, `Cannot set localStorage key "${key}" when window is undefined`);
        return;
      }

      try {
        // Allow value to be a function to match useState API
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        
        // Save state
        setStoredValue(valueToStore);
        
        // Déterminer comment stocker la valeur
        let storageValue;
        if (typeof valueToStore === 'string') {
          // Si c'est déjà une chaîne, on peut la stocker directement
          storageValue = valueToStore;
        } else {
          // Sinon, on utilise JSON.stringify
          storageValue = JSON.stringify(valueToStore);
        }
        
        // Save to localStorage
        window.localStorage.setItem(key, storageValue);
        
        // Dispatch a custom event so other instances can update
        window.dispatchEvent(new Event('local-storage-change'));
      } catch (error) {
        debug.warn(config, `Error setting localStorage key "${key}":`, error);
      }
    },
    [key, storedValue]
  );

  // Remove from localStorage
  const removeValue = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      // Remove from localStorage
      window.localStorage.removeItem(key);
      
      // Set state to initial value
      setStoredValue(initialValue);
      
      // Dispatch a custom event so other instances can update
      window.dispatchEvent(new Event('local-storage-change'));
    } catch (error) {
      debug.warn(config, `Error removing localStorage key "${key}":`, error);
    }
  }, [initialValue, key]);

  // Listen for changes to this localStorage key in other windows/tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === key || e.type === 'local-storage-change') {
        setStoredValue(readValue());
      }
    };

    // Listen for localStorage changes and custom events
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('local-storage-change', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('local-storage-change', handleStorageChange);
    };
  }, [key, readValue]);

  return [storedValue, setValue, removeValue];
}
